import { Injectable, inject, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { EnvironmentService } from '../shared/environment.service';
import { FirebaseAuthService } from '../shared/firebase-auth.service';
import { ClientLoggingService } from '../shared/client-logging.service';
import { AudioMuteService } from '../shared/audio-mute.service';
import {
  VoiceChatState,
  VoiceChatConfig,
  VoiceResponse,
  ChatContext,
  VoiceHistoryEntry,
  VoiceTokenUsage,
  VoiceTranscriptCorrectedEvent,
  VoiceTranscriptDecoratedEvent,
} from '../models/chat.models';
import { firstValueFrom } from 'rxjs';

const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
const KEEPALIVE_INTERVAL_MS = 25_000;
const LOG_TAG = '[VoiceChat]';
const VOICE_ACTIVITY_THRESHOLD = 0.005;
const VOICE_END_SILENCE_MS = 1_500;

export type VoiceTurnPhase = 'listening' | 'processing';

/**
 * RMS threshold for echo gating (Issue #1214). When the model is speaking, mic audio
 * chunks with RMS below this level are suppressed to prevent the model's own output
 * (picked up by the loudspeaker) from triggering false barge-in. Audio above this
 * threshold is still sent, preserving deliberate barge-in.
 */
export const ECHO_GATE_THRESHOLD = 0.06;

/** How often to log echo-gated chunk counts (every N suppressed chunks). */
const LOG_ECHO_GATE_INTERVAL = 50;

/** Seconds of silence after which the inactivity countdown appears in the UI. */
const SILENCE_DISPLAY_THRESHOLD_SECONDS = 15;

/** Total inactivity budget (seconds). Server manages the actual disconnect. */
const INACTIVITY_BUDGET_SECONDS = 300; // 5 min

/**
 * Maps technical error messages to user-friendly descriptions with actionable guidance.
 * The raw technical message is preserved in logs; only the display message is translated.
 */
export function toUserFriendlyError(error: unknown): string {
  // String(error) includes the error name (e.g. "NotAllowedError: ...") which is
  // essential for matching DOMExceptions whose .message omits the name.
  const full = String(error);
  const lower = full.toLowerCase();

  if (lower.includes('worklet') && lower.includes('module')) {
    return 'Could not start voice chat — please check your internet connection and try again.';
  }
  if (
    lower.includes('networkerror') ||
    lower.includes('net::err_internet_disconnected') ||
    lower.includes('failed to fetch')
  ) {
    return 'Network error — please check your internet connection and try again.';
  }
  if (lower.includes('not allowed') || lower.includes('permission denied') || lower.includes('notallowederror')) {
    return 'Microphone access was denied. Please allow microphone permission in your browser settings and try again.';
  }
  if (lower.includes('not found') || lower.includes('notfounderror') || lower.includes('requested device not found')) {
    return 'No microphone found. Please connect a microphone and try again.';
  }
  if (lower.includes('websocket') || lower.includes('connection failed')) {
    return 'Could not connect to the voice server — please check your internet connection and try again.';
  }

  if (error == null) {
    return 'An unexpected error occurred. Please try again.';
  }
  const fallback = error instanceof Error ? error.message : full;
  return fallback?.trim() || 'An unexpected error occurred. Please try again.';
}

/**
 * Issue #818: Client-to-server reconnect for voice WebSocket.
 * Modeled on ChatService's Issue #733 reconnect pattern.
 */
const RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_ATTEMPTS = 3;
const MAX_PENDING_AUDIO_CHUNKS = 50;

/**
 * Service for bidirectional live voice chat using Gemini Live API.
 *
 * Handles:
 * - Microphone capture via AudioWorklet (16-bit PCM, 16kHz, mono)
 * - Audio playback via AudioContext (24kHz PCM)
 * - WebSocket connection to /ws/voice
 * - Session lifecycle (start → active → stop)
 * - Real-time audio level metering for the VU indicator
 *
 * Issue #802
 */
@Injectable({ providedIn: 'root' })
export class VoiceChatService {
  private env = inject(EnvironmentService);
  private auth = inject(FirebaseAuthService);
  private zone = inject(NgZone);
  private audioMuteService = inject(AudioMuteService);

  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;

  // Playback pipeline — uses a GainNode gated by AudioMuteService
  private playbackContext: AudioContext | null = null;
  private playbackGain: GainNode | null = null;
  private nextPlaybackTime = 0;
  private activePlaybackSources: AudioBufferSourceNode[] = [];
  private muteSub: Subscription | null = null;

  // Echo suppression (Issue #1214): tracks whether model audio is currently playing
  // so the mic can be gated to prevent the model's own output from triggering barge-in.
  private modelSpeaking = false;
  private echoGatedChunks = 0;

  // Connection audio cues (Issue #1217): two distinct tones that span the
  // full connection lifecycle so the user never hears silence while waiting.
  private connectingToneCtx: AudioContext | null = null;
  private connectingToneGain: GainNode | null = null;
  private connectingToneTimer: ReturnType<typeof setTimeout> | null = null;
  private awaitingModelToneCtx: AudioContext | null = null;
  private awaitingModelToneGain: GainNode | null = null;
  private firstAudioReceived = false;

  // Issue #818: Reconnect state — mirrors ChatService Issue #733 pattern
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private manualDisconnect = false;
  private backendReady = false;
  private pendingAudioChunks: ArrayBuffer[] = [];
  private speechActivityActive = false;
  private silenceStartedAt: number | null = null;
  private pendingSpeechDetected = false;
  private lastSessionId: string | null = null;
  private lastConfig?: VoiceChatConfig;
  private lastContext?: ChatContext;

  private readonly stateSubject = new BehaviorSubject<VoiceChatState>('idle');
  private readonly mutedSubject = new BehaviorSubject<boolean>(false);
  private readonly audioLevelSubject = new BehaviorSubject<number>(0);
  private readonly transcriptSubject = new Subject<{
    role: 'user' | 'assistant';
    text: string;
    partial: boolean;
  }>();
  private readonly errorSubject = new Subject<string>();
  private readonly toolCallSubject = new Subject<VoiceResponse>();
  private readonly thinkingSubject = new Subject<string>();
  private readonly tokenUsageSubject = new Subject<VoiceTokenUsage>();
  private readonly reconnectedSubject = new Subject<void>();
  /** Issue #3356: the model finished a turn; its next speech is a new reply. */
  private readonly turnCompleteSubject = new Subject<void>();
  private readonly transcriptDecoratedSubject = new Subject<VoiceTranscriptDecoratedEvent>();
  private readonly transcriptCorrectedSubject = new Subject<VoiceTranscriptCorrectedEvent>();
  private readonly turnPhaseSubject = new BehaviorSubject<VoiceTurnPhase>('listening');

  private audioChunksSent = 0;
  private audioChunksFromWorklet = 0;

  // Session duration / inactivity timers
  private sessionStartTime: number | null = null;
  private lastAudioActivityTime: number | null = null;
  private uiTimerInterval: ReturnType<typeof setInterval> | null = null;
  private readonly sessionDurationSubject = new BehaviorSubject<number>(0);
  private readonly inactivityCountdownSubject = new BehaviorSubject<number | null>(null);

  get state$(): Observable<VoiceChatState> {
    return this.stateSubject.asObservable();
  }

  get muted$(): Observable<boolean> {
    return this.mutedSubject.asObservable();
  }

  /** Real-time microphone audio level (0..1) for VU meter display. */
  get audioLevel$(): Observable<number> {
    return this.audioLevelSubject.asObservable();
  }

  get transcript$(): Observable<{ role: 'user' | 'assistant'; text: string; partial: boolean }> {
    return this.transcriptSubject.asObservable();
  }

  get error$(): Observable<string> {
    return this.errorSubject.asObservable();
  }

  get toolCall$(): Observable<VoiceResponse> {
    return this.toolCallSubject.asObservable();
  }

  get thinking$(): Observable<string> {
    return this.thinkingSubject.asObservable();
  }

  /** Emits context fill updates from voice chat usage metadata (Issue #919). */
  get tokenUsage$(): Observable<VoiceTokenUsage> {
    return this.tokenUsageSubject.asObservable();
  }

  /** Emits after a successful WebSocket reconnection (Issue #869). */
  get reconnected$(): Observable<void> {
    return this.reconnectedSubject.asObservable();
  }

  get turnComplete$(): Observable<void> {
    return this.turnCompleteSubject.asObservable();
  }

  /** Emits when an async-decorated transcript arrives from the server (Issue #941). */
  get transcriptDecorated$(): Observable<VoiceTranscriptDecoratedEvent> {
    return this.transcriptDecoratedSubject.asObservable();
  }

  /** Emits when a corrected user transcript arrives from the server (Issue #880). */
  get transcriptCorrected$(): Observable<VoiceTranscriptCorrectedEvent> {
    return this.transcriptCorrectedSubject.asObservable();
  }

  get turnPhase$(): Observable<VoiceTurnPhase> {
    return this.turnPhaseSubject.asObservable();
  }

  /** Elapsed seconds since the voice session started (updates every second). */
  get sessionDuration$(): Observable<number> {
    return this.sessionDurationSubject.asObservable();
  }

  /**
   * Seconds remaining until inactivity disconnect. Null when the user is actively speaking
   * (or silence has not exceeded the display threshold).
   */
  get inactivityCountdown$(): Observable<number | null> {
    return this.inactivityCountdownSubject.asObservable();
  }

  get currentState(): VoiceChatState {
    return this.stateSubject.value;
  }

  get isMuted(): boolean {
    return this.mutedSubject.value;
  }

  /**
   * Mutes or unmutes the microphone. When muted, the MediaStream audio
   * tracks are disabled so no PCM data reaches the worklet, preventing
   * background noise from triggering false barge-in interruptions.
   *
   * On unmute, AudioContexts are resumed in case the browser suspended them
   * during the mute period (e.g. tab backgrounded or prolonged silence).
   */
  setMuted(muted: boolean): void {
    this.mutedSubject.next(muted);
    if (this.mediaStream) {
      for (const track of this.mediaStream.getAudioTracks()) {
        track.enabled = !muted;
      }
    }
    if (muted) {
      if (this.speechActivityActive) {
        this.sendActivityBoundary('activity_end');
        this.speechActivityActive = false;
        this.silenceStartedAt = null;
        this.turnPhaseSubject.next('processing');
      }
      this.audioLevelSubject.next(0);
    } else {
      this.ensureAudioContextsRunning();
    }
    ClientLoggingService.log('INFO', `${LOG_TAG} Mic ${muted ? 'muted' : 'unmuted'}`);

    // Notify the server so mute/unmute events appear in server logs even when
    // the client-to-backend logging pipeline (ESPv2 /v1/client-logs) is down.
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'mute_state', muted }));
    }
  }

  toggleMute(): void {
    this.setMuted(!this.mutedSubject.value);
  }

  /**
   * Starts a live voice session. Requests mic permission, sets up AudioWorklet,
   * connects WebSocket, and begins streaming audio bidirectionally.
   */
  async start(
    sessionId: string,
    config?: VoiceChatConfig,
    context?: ChatContext,
    conversationHistory?: VoiceHistoryEntry[]
  ): Promise<void> {
    ClientLoggingService.log(
      'INFO',
      `${LOG_TAG} start() called: sessionId=${sessionId}, currentState=${this.stateSubject.value}`
    );

    if (
      this.stateSubject.value === 'active' ||
      this.stateSubject.value === 'connecting' ||
      this.stateSubject.value === 'reconnecting'
    ) {
      ClientLoggingService.log('WARN', `${LOG_TAG} Cannot start: already ${this.stateSubject.value}`);
      return;
    }

    if (this.stateSubject.value === 'error') {
      ClientLoggingService.log('INFO', `${LOG_TAG} Cleaning up previous error state`);
      this.cleanup();
    }

    this.stateSubject.next('connecting');
    this.audioChunksSent = 0;
    this.audioChunksFromWorklet = 0;
    this.serverMessageCount = 0;
    this.echoGatedChunks = 0;
    this.modelSpeaking = false;
    this.firstAudioReceived = false;
    this.backendReady = false;
    this.pendingAudioChunks = [];
    this.resetActivityBoundaryState();
    this.startConnectingTone();

    try {
      ClientLoggingService.log('INFO', `${LOG_TAG} Requesting microphone access...`);
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: INPUT_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const tracks = this.mediaStream.getAudioTracks();
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} Mic acquired: ${tracks.length} track(s), ` + `settings=${JSON.stringify(tracks[0]?.getSettings())}`
      );

      ClientLoggingService.log('INFO', `${LOG_TAG} Creating AudioContext (${INPUT_SAMPLE_RATE}Hz)...`);
      this.audioContext = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} AudioContext state=${this.audioContext.state}, sampleRate=${this.audioContext.sampleRate}`
      );

      await this.audioContext.audioWorklet.addModule('assets/audio-worklet-processor.js');
      ClientLoggingService.log('INFO', `${LOG_TAG} AudioWorklet module loaded`);

      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-capture-processor');

      this.workletNode.port.onmessage = (event: MessageEvent) => {
        const { pcm, level } = event.data;
        this.handleWorkletAudioChunk(pcm as ArrayBuffer, level as number);
      };

      source.connect(this.workletNode);
      ClientLoggingService.log('INFO', `${LOG_TAG} Audio pipeline: mic → worklet (capture only, no feedback)`);

      this.playbackContext = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
      this.playbackGain = this.playbackContext.createGain();
      this.playbackGain.gain.value = this.audioMuteService.isMuted ? 0 : 1;
      this.playbackGain.connect(this.playbackContext.destination);
      this.muteSub = this.audioMuteService.muted$.subscribe(muted => {
        if (this.playbackGain) {
          this.playbackGain.gain.value = muted ? 0 : 1;
        }
      });
      this.nextPlaybackTime = 0;
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} Playback AudioContext: state=${this.playbackContext.state}, rate=${this.playbackContext.sampleRate}`
      );

      await this.connectWebSocket(sessionId, config, context, conversationHistory);

      this.mutedSubject.next(false);
      ClientLoggingService.log('INFO', `${LOG_TAG} Voice session fully started`);
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : String(err);
      ClientLoggingService.log(
        'ERROR',
        `${LOG_TAG} start() FAILED: ${rawMessage}`,
        err instanceof Error ? err.stack : err
      );
      this.errorSubject.next(toUserFriendlyError(err));
      this.cleanup();
      this.stateSubject.next('error');
    }
  }

  stop(): void {
    if (this.stateSubject.value === 'idle') return;

    ClientLoggingService.log(
      'INFO',
      `${LOG_TAG} stop() called. State=${this.stateSubject.value}, ` +
        `audioChunksSent=${this.audioChunksSent}, ` +
        `workletChunks=${this.audioChunksFromWorklet}, ` +
        `serverMsgs=${this.serverMessageCount}, ` +
        `echoGated=${this.echoGatedChunks}`
    );

    this.playHangupTone();
    this.manualDisconnect = true;
    this.cancelPendingReconnect();

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'stop' }));
    }

    this.cleanup();
    this.mutedSubject.next(false);
    this.audioLevelSubject.next(0);
    this.stateSubject.next('idle');
  }

  private lastConversationHistory?: VoiceHistoryEntry[];

  private async connectWebSocket(
    sessionId: string,
    config?: VoiceChatConfig,
    context?: ChatContext,
    conversationHistory?: VoiceHistoryEntry[],
    isReconnect = false
  ): Promise<void> {
    // Issue #818: Store session params for potential reconnection
    this.lastSessionId = sessionId;
    this.lastConfig = config;
    this.lastContext = context;
    this.lastConversationHistory = conversationHistory;
    this.manualDisconnect = false;
    this.backendReady = false;
    this.resetActivityBoundaryState();

    ClientLoggingService.log('INFO', `${LOG_TAG} connectWebSocket: authenticating...`);
    const user = await firstValueFrom(this.auth.user$);
    if (!user) throw new Error('Not authenticated');

    const token = await user.getIdToken();
    const wsServer = this.env.getWsServer();
    ClientLoggingService.log('INFO', `${LOG_TAG} wsServer=${wsServer}`);

    let wsBase: string;
    if (!wsServer || wsServer.includes('localhost')) {
      wsBase = 'ws://localhost:8081';
    } else {
      wsBase = `wss://${wsServer}`;
    }

    const wsUrl = `${wsBase}/ws/voice?session_id=${sessionId}`;
    ClientLoggingService.log('INFO', `${LOG_TAG} Connecting WebSocket to ${wsBase}/ws/voice...`);

    return new Promise<void>((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);
      let hasResolved = false;

      this.ws.onopen = () => {
        ClientLoggingService.log('INFO', `${LOG_TAG} WebSocket OPEN, sending auth...`);
        this.ws!.send(JSON.stringify({ type: 'auth', token }));
      };

      this.ws.onmessage = (event: MessageEvent) => {
        let parsed: VoiceResponse;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          ClientLoggingService.log(
            'WARN',
            `${LOG_TAG} Non-JSON message (${(event.data as string).length} chars): '${String(event.data).substring(0, 200)}'`
          );
          return;
        }

        if (parsed?.type === 'auth_ok') {
          ClientLoggingService.log('INFO', `${LOG_TAG} Authenticated, sending start command`);
          const startPayload: Record<string, unknown> = {
            type: 'start',
            sessionId,
            config: {
              voice: config?.voice || 'Aoede',
              language: config?.language || 'en-US',
              model: config?.model,
            },
          };
          if (context) {
            startPayload['context'] = context;
          }
          if (conversationHistory && conversationHistory.length > 0) {
            startPayload['conversationHistory'] = conversationHistory;
          }
          if (isReconnect) {
            startPayload['reconnect'] = true;
          }
          const safeLogPayload: Record<string, unknown> = {
            type: startPayload['type'],
            sessionId: startPayload['sessionId'],
            config: startPayload['config'],
            hasContext: !!startPayload['context'],
            conversationHistoryCount: Array.isArray(startPayload['conversationHistory'])
              ? (startPayload['conversationHistory'] as unknown[]).length
              : 0,
          };
          ClientLoggingService.log('INFO', `${LOG_TAG} Start payload: ${JSON.stringify(safeLogPayload)}`);
          this.ws!.send(JSON.stringify(startPayload));
          this.startKeepalive();
          if (!isReconnect) {
            this.startUiTimers();
          }
          hasResolved = true;
          resolve();
          return;
        }

        this.zone.run(() => this.handleServerMessage(parsed));
      };

      this.ws.onerror = () => {
        const previousState = this.stateSubject.value;
        ClientLoggingService.log(
          'ERROR',
          `${LOG_TAG} WebSocket ERROR event. readyState=${this.ws?.readyState}, prevState=${previousState}`
        );
        this.stopKeepalive();

        // Preserve the audio pipeline and schedule recovery immediately. A following onclose
        // event observes the existing timer and cannot create a duplicate attempt.
        if (previousState === 'active' || previousState === 'reconnecting') {
          this.scheduleReconnect();
        } else {
          const friendlyMsg = navigator.onLine
            ? 'Voice connection error — please try again.'
            : 'You appear to be offline. Please check your internet connection and try again.';
          this.errorSubject.next(friendlyMsg);
          this.stateSubject.next('error');
          if (previousState === 'connecting') {
            this.cleanup();
            this.audioLevelSubject.next(0);
          }
        }
        if (!hasResolved) {
          hasResolved = true;
          reject(new Error('WebSocket connection failed'));
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        const code = event.code;
        ClientLoggingService.log(
          'INFO',
          `${LOG_TAG} WebSocket CLOSED: code=${code}, reason='${event.reason}', ` +
            `wasClean=${event.wasClean}, audioChunksSent=${this.audioChunksSent}, ` +
            `serverMsgsReceived=${this.serverMessageCount}`
        );
        this.stopKeepalive();

        // Issue #818: Reconnect on abnormal closures (network drop, proxy timeout)
        // Codes: 1006 (abnormal), 1001 (going away), 1005 (no status received)
        const reconnectable = [1006, 1001, 1005];
        const currentState = this.stateSubject.value;
        const canReconnect =
          currentState === 'active' || currentState === 'connecting' || currentState === 'reconnecting';
        if (!this.manualDisconnect && canReconnect && reconnectable.includes(code)) {
          this.scheduleReconnect();
        } else if (canReconnect) {
          this.cleanup();
          this.audioLevelSubject.next(0);
          this.stateSubject.next('idle');
        }

        if (!hasResolved) {
          hasResolved = true;
          reject(new Error(`WebSocket closed during auth: code=${code}, reason=${event.reason}`));
        }
      };
    });
  }

  private serverMessageCount = 0;

  private handleServerMessage(response: VoiceResponse): void {
    this.serverMessageCount++;

    if (this.serverMessageCount <= 10 || this.serverMessageCount % 25 === 0) {
      let preview = '';
      switch (response.type) {
        case 'audio':
          preview = `data.len=${response.data?.length ?? 0}`;
          break;
        case 'transcript':
          preview = `role=${response.role}, partial=${response.partial}, len=${response.text?.length ?? 0}`;
          break;
        case 'status':
          preview = `state=${response.state}`;
          break;
        case 'error':
          preview = `msg=${response.message}`;
          break;
        case 'tool_call_start':
          preview = `tool=${response.toolName}, opId=${response.operationId}`;
          break;
        case 'tool_call_result':
          preview = `tool=${response.toolName}`;
          break;
        case 'thinking':
          preview = `len=${response.text?.length ?? 0}`;
          break;
      }
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} Server msg #${this.serverMessageCount}: type=${response.type}, ${preview}`
      );
    }

    switch (response.type) {
      case 'audio':
        if (response.data) {
          if (!this.firstAudioReceived) {
            this.firstAudioReceived = true;
            this.turnPhaseSubject.next('listening');
            this.stopAwaitingModelTone();
            ClientLoggingService.log('INFO', `${LOG_TAG} First audio chunk — stopped awaiting-model tone`);
          }
          this.playAudioChunk(response.data);
        }
        break;

      case 'interrupted':
        ClientLoggingService.log('INFO', `${LOG_TAG} Barge-in detected, flushing playback`);
        this.flushPlaybackBuffer();
        break;

      case 'transcript':
        ClientLoggingService.log(
          'INFO',
          `${LOG_TAG} TRANSCRIPT: role=${response.role}, partial=${response.partial}, len=${response.text?.length ?? 0}`
        );
        if (response.text && response.role) {
          this.transcriptSubject.next({
            role: response.role,
            text: response.text,
            partial: response.partial === true || response.partial === 'true',
          });
        } else {
          ClientLoggingService.log('WARN', `${LOG_TAG} Skipped transcript: missing text or role`);
        }
        break;

      case 'status':
        ClientLoggingService.log('INFO', `${LOG_TAG} Status update: state=${response.state}`);
        if (response.state === 'listening') {
          this.turnPhaseSubject.next('listening');
          const resumed = response.resumed === true;
          this.backendReady = true;
          this.stateSubject.next('active');
          this.reconnectAttempts = 0;
          this.flushPendingAudio();
          if (resumed) {
            this.reconnectedSubject.next();
          }
          this.stopConnectingTone();
          if (!this.firstAudioReceived) {
            this.startAwaitingModelTone();
            ClientLoggingService.log('INFO', `${LOG_TAG} Backend ready — switched to awaiting-model tone`);
          }
        } else if (response.state === 'processing') {
          this.turnPhaseSubject.next('processing');
        } else if (response.state === 'ended') {
          this.stop();
        }
        break;

      case 'error':
        ClientLoggingService.log('ERROR', `${LOG_TAG} Server error: ${response.message}`);
        this.errorSubject.next(response.message || 'Voice chat error');
        this.cleanup();
        this.audioLevelSubject.next(0);
        this.stateSubject.next('error');
        break;

      case 'tool_call_start':
        ClientLoggingService.log('INFO', `${LOG_TAG} Tool call start: ${response.toolName} (${response.operationId})`);
        this.playProcessingTone();
        this.toolCallSubject.next(response);
        break;

      case 'tool_call_result':
        ClientLoggingService.log('INFO', `${LOG_TAG} Tool call result: ${response.toolName}`);
        this.playCompletionTone();
        this.toolCallSubject.next(response);
        break;

      case 'thinking':
        ClientLoggingService.log('INFO', `${LOG_TAG} Thinking: len=${response.text?.length ?? 0}`);
        if (response.text) {
          this.thinkingSubject.next(response.text);
        }
        break;

      case 'transcript_decorated':
        if (response.messageId && response.content && response.rawContent) {
          this.transcriptDecoratedSubject.next({
            messageId: response.messageId,
            content: response.content,
            rawContent: response.rawContent,
          });
        }
        break;

      case 'transcript_corrected':
        ClientLoggingService.log(
          'INFO',
          `${LOG_TAG} TRANSCRIPT_CORRECTED:` +
            ` messageId=${response.messageId},` +
            ` rawLen=${response.rawContent?.length ?? 0},` +
            ` correctedLen=${response.content?.length ?? 0},` +
            ` changed=${response.rawContent !== response.content}`
        );
        if (response.content && response.rawContent) {
          this.transcriptCorrectedSubject.next({
            messageId: response.messageId || '',
            content: response.content,
            rawContent: response.rawContent,
          });
        } else {
          ClientLoggingService.log(
            'WARN',
            `${LOG_TAG} TRANSCRIPT_CORRECTED missing fields:` +
              ` content=${!!response.content},` +
              ` rawContent=${!!response.rawContent}`
          );
        }
        break;

      case 'token_usage': {
        const usage = response as VoiceResponse & {
          promptTokens?: number;
          candidatesTokens?: number;
          totalTokens?: number;
          contextWindowSize?: number;
          estimatedTotalCostUsd?: number;
          model?: string;
        };
        this.tokenUsageSubject.next({
          promptTokens: usage.promptTokens ?? 0,
          candidatesTokens: usage.candidatesTokens ?? 0,
          totalTokens: usage.totalTokens ?? 0,
          contextWindowSize: usage.contextWindowSize ?? 0,
          estimatedTotalCostUsd: usage.estimatedTotalCostUsd ?? 0,
          model: usage.model,
        });
        break;
      }

      case 'turn_complete':
        // Issue #3356: the server stores each model turn as its own message, so the chat starts
        // a new bubble for whatever the model says next (ChatComponent.onVoiceTurnComplete).
        ClientLoggingService.log('INFO', `${LOG_TAG} Turn complete`);
        this.turnCompleteSubject.next();
        break;

      case 'pong':
        break;

      case 'inactivity_warning':
        ClientLoggingService.log('INFO', `${LOG_TAG} Inactivity warning: ${response.secondsRemaining}s remaining`);
        if (response.secondsRemaining != null) {
          // Recalibrate the local inactivity clock to match the server's timeline,
          // preventing the next 1s tick from overwriting this value with a stale estimate.
          this.lastAudioActivityTime = Date.now() - (INACTIVITY_BUDGET_SECONDS - response.secondsRemaining) * 1000;
          this.inactivityCountdownSubject.next(response.secondsRemaining);
        }
        break;

      default:
        ClientLoggingService.log('WARN', `${LOG_TAG} Unknown message type: '${response.type}'`);
    }
  }

  private sendActivityBoundary(type: 'activity_start' | 'activity_end'): void {
    if (!this.backendReady || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type }));
  }

  private updateActivityBoundary(level: number): void {
    if (!this.backendReady) {
      if (level >= VOICE_ACTIVITY_THRESHOLD) this.pendingSpeechDetected = true;
      return;
    }

    const now = Date.now();
    if (level >= VOICE_ACTIVITY_THRESHOLD) {
      this.silenceStartedAt = null;
      if (!this.speechActivityActive) {
        this.sendActivityBoundary('activity_start');
        this.speechActivityActive = true;
        this.turnPhaseSubject.next('listening');
      }
      return;
    }

    if (!this.speechActivityActive) return;
    this.silenceStartedAt ??= now;
    if (now - this.silenceStartedAt >= VOICE_END_SILENCE_MS) {
      this.sendActivityBoundary('activity_end');
      this.speechActivityActive = false;
      this.silenceStartedAt = null;
      this.turnPhaseSubject.next('processing');
    }
  }

  private resetActivityBoundaryState(): void {
    this.speechActivityActive = false;
    this.silenceStartedAt = null;
    this.pendingSpeechDetected = false;
    this.turnPhaseSubject.next('listening');
  }

  private sendAudioChunk(pcmBuffer: ArrayBuffer): void {
    if (!this.backendReady || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.pendingAudioChunks.length >= MAX_PENDING_AUDIO_CHUNKS) {
        this.pendingAudioChunks.shift();
      }
      this.pendingAudioChunks.push(pcmBuffer.slice(0));
      if (this.pendingAudioChunks.length === 1) {
        ClientLoggingService.log('INFO', LOG_TAG + ' Buffering microphone audio until backend readiness');
      }
      return;
    }

    const bytes = new Uint8Array(pcmBuffer);
    const base64 = this.uint8ToBase64(bytes);
    this.audioChunksSent++;
    this.resetInactivityTimer();

    if (this.audioChunksSent <= 3 || this.audioChunksSent % 100 === 0) {
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} Sending audio chunk #${this.audioChunksSent}: ` +
          `pcm=${pcmBuffer.byteLength}B, base64=${base64.length} chars, ` +
          `wsState=${this.ws.readyState}, buffered=${this.ws.bufferedAmount}`
      );
    }

    this.ws.send(JSON.stringify({ type: 'audio', data: base64 }));
  }

  private flushPendingAudio(): void {
    const chunks = this.pendingAudioChunks;
    this.pendingAudioChunks = [];
    if (chunks.length > 0 && this.pendingSpeechDetected && !this.speechActivityActive) {
      this.sendActivityBoundary('activity_start');
      this.speechActivityActive = true;
    }
    this.pendingSpeechDetected = false;
    for (const chunk of chunks) {
      this.sendAudioChunk(chunk);
    }
    if (chunks.length > 0) {
      ClientLoggingService.log('INFO', LOG_TAG + ' Flushed ' + chunks.length + ' buffered audio chunk(s)');
    }
  }

  private playAudioChunk(base64Data: string): void {
    if (!this.playbackContext) return;

    const pcmBytes = this.base64ToUint8(base64Data);
    const int16 = new Int16Array(pcmBytes.buffer);
    const float32 = new Float32Array(int16.length);

    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
    }

    const audioBuffer = this.playbackContext.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
    audioBuffer.getChannelData(0).set(float32);

    const source = this.playbackContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.playbackGain || this.playbackContext.destination);

    const now = this.playbackContext.currentTime;
    const startTime = Math.max(now, this.nextPlaybackTime);
    source.start(startTime);
    this.nextPlaybackTime = startTime + audioBuffer.duration;

    this.modelSpeaking = true;
    this.activePlaybackSources.push(source);
    source.onended = () => {
      const idx = this.activePlaybackSources.indexOf(source);
      if (idx !== -1) this.activePlaybackSources.splice(idx, 1);
      if (this.activePlaybackSources.length === 0) {
        this.modelSpeaking = false;
      }
    };
  }

  private flushPlaybackBuffer(): void {
    for (const source of this.activePlaybackSources) {
      try {
        source.stop();
      } catch {
        // source may already have finished
      }
    }
    this.activePlaybackSources = [];
    this.nextPlaybackTime = 0;
    this.modelSpeaking = false;
  }

  /**
   * Processes an audio chunk from the AudioWorklet. Applies echo gating when the model
   * is playing audio: chunks with RMS below {@link ECHO_GATE_THRESHOLD} are suppressed
   * to avoid the loudspeaker echo triggering barge-in. Chunks above the threshold are
   * forwarded, preserving deliberate user interruption.
   */
  private handleWorkletAudioChunk(pcm: ArrayBuffer, level: number): void {
    this.audioChunksFromWorklet++;
    if (this.audioChunksFromWorklet <= 3 || this.audioChunksFromWorklet % 100 === 0) {
      ClientLoggingService.log(
        'INFO',
        `${LOG_TAG} Worklet chunk #${this.audioChunksFromWorklet}: ` +
          `pcmBytes=${pcm.byteLength}, level=${level.toFixed(4)}, ` +
          `muted=${this.mutedSubject.value}, modelSpeaking=${this.modelSpeaking}`
      );
    }
    this.zone.run(() => this.audioLevelSubject.next(level));
    if (!this.mutedSubject.value) {
      if (this.modelSpeaking && level < ECHO_GATE_THRESHOLD) {
        this.echoGatedChunks++;
        if (this.echoGatedChunks === 1 || this.echoGatedChunks % LOG_ECHO_GATE_INTERVAL === 0) {
          ClientLoggingService.log(
            'INFO',
            `${LOG_TAG} Echo gate suppressed chunk (total=${this.echoGatedChunks}, level=${level.toFixed(4)})`
          );
        }
      } else {
        this.updateActivityBoundary(level);
        this.sendAudioChunk(pcm);
      }
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepaliveTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  private startUiTimers(): void {
    this.stopUiTimers();
    const now = Date.now();
    this.sessionStartTime = now;
    this.lastAudioActivityTime = now;
    this.sessionDurationSubject.next(0);
    this.inactivityCountdownSubject.next(null);

    this.uiTimerInterval = setInterval(() => {
      const nowMs = Date.now();
      if (this.sessionStartTime) {
        this.sessionDurationSubject.next(Math.floor((nowMs - this.sessionStartTime) / 1000));
      }

      if (this.lastAudioActivityTime) {
        const silenceSec = (nowMs - this.lastAudioActivityTime) / 1000;
        if (silenceSec >= SILENCE_DISPLAY_THRESHOLD_SECONDS) {
          const remaining = Math.max(0, Math.round(INACTIVITY_BUDGET_SECONDS - silenceSec));
          this.inactivityCountdownSubject.next(remaining);
        } else {
          this.inactivityCountdownSubject.next(null);
        }
      }
    }, 1000);
  }

  private stopUiTimers(): void {
    if (this.uiTimerInterval) {
      clearInterval(this.uiTimerInterval);
      this.uiTimerInterval = null;
    }
    this.sessionStartTime = null;
    this.lastAudioActivityTime = null;
    this.sessionDurationSubject.next(0);
    this.inactivityCountdownSubject.next(null);
  }

  /** Called when audio activity is detected (user speaking). Resets the inactivity clock. */
  private resetInactivityTimer(): void {
    this.lastAudioActivityTime = Date.now();
    this.inactivityCountdownSubject.next(null);
  }

  /**
   * Issue #2796: Schedules a reconnection attempt with bounded exponential backoff. Handles the max-attempts
   * check internally so callers don't need to guard it. On failure, schedules the next retry
   * if attempts remain; otherwise emits an error and tears down.
   */
  private scheduleReconnect(): void {
    if (!this.lastSessionId || this.reconnectTimer) return;

    this.backendReady = false;
    this.stateSubject.next('reconnecting');
    this.reconnectAttempts++;
    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      ClientLoggingService.log('ERROR', `${LOG_TAG} Max reconnect attempts reached, giving up`);
      this.cleanup();
      this.audioLevelSubject.next(0);
      this.stateSubject.next('error');
      this.errorSubject.next('Voice connection lost — please check your internet connection and try again.');
      return;
    }

    const delay = RECONNECT_DELAY_MS * 2 ** (this.reconnectAttempts - 1);
    ClientLoggingService.log(
      'INFO',
      `${LOG_TAG} Scheduling reconnect attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    const timer = setTimeout(async () => {
      if (this.reconnectTimer !== timer) return;
      if (this.manualDisconnect || !this.lastSessionId) {
        this.reconnectTimer = undefined;
        return;
      }

      try {
        this.cleanupWebSocketOnly();
        await this.connectWebSocket(
          this.lastSessionId,
          this.lastConfig,
          this.lastContext,
          this.lastConversationHistory,
          true
        );
        if (this.reconnectTimer === timer) {
          this.reconnectTimer = undefined;
        }
        ClientLoggingService.log('INFO', LOG_TAG + ' Reconnect transport authenticated; awaiting backend resume');
      } catch (err) {
        if (this.reconnectTimer !== timer) {
          return;
        }
        this.reconnectTimer = undefined;
        ClientLoggingService.log('WARN', `${LOG_TAG} Reconnect failed: ${err}`);
        this.scheduleReconnect();
      }
    }, delay);
    this.reconnectTimer = timer;
  }

  private cancelPendingReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Closes only the WebSocket without tearing down the audio pipeline, so the mic and playback
   * contexts survive across reconnection attempts.
   */
  private cleanupWebSocketOnly(): void {
    this.stopKeepalive();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
  }

  /** Short low-frequency tone to signal a tool call has started. */
  private playProcessingTone(): void {
    if (!this.playbackContext || this.playbackContext.state === 'closed') return;
    const osc = this.playbackContext.createOscillator();
    const gain = this.playbackContext.createGain();
    osc.frequency.value = 440;
    osc.type = 'sine';
    gain.gain.value = 0.08;
    osc.connect(gain);
    gain.connect(this.playbackGain || this.playbackContext.destination);
    const now = this.playbackContext.currentTime;
    osc.start(now);
    osc.stop(now + 0.15);
  }

  /**
   * Starts a looping "ringing" arpeggio (C5→E5→G5) that plays while the WebSocket
   * connection and ADK session are being established (Issue #1217).
   * Uses a standalone AudioContext because the playback pipeline isn't ready yet.
   */
  private startConnectingTone(ctxFactory: () => AudioContext = () => new AudioContext()): void {
    this.stopConnectingTone();
    try {
      const ctx = ctxFactory();
      this.connectingToneCtx = ctx;
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.09;
      masterGain.connect(ctx.destination);
      this.connectingToneGain = masterGain;

      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
      const noteDuration = 0.12;
      const noteGap = 0.06;
      const cycleLength = notes.length * (noteDuration + noteGap);
      const pauseBetweenCycles = 0.36;
      const loopInterval = cycleLength + pauseBetweenCycles;

      this.zone.runOutsideAngular(() => {
        const scheduleLoop = () => {
          if (!this.connectingToneCtx || this.connectingToneCtx !== ctx) return;
          const baseTime = ctx.currentTime + 0.01;
          notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const env = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;

            const start = baseTime + i * (noteDuration + noteGap);
            env.gain.setValueAtTime(0, start);
            env.gain.linearRampToValueAtTime(1, start + 0.02);
            env.gain.linearRampToValueAtTime(0, start + noteDuration);

            osc.connect(env);
            env.connect(masterGain);
            osc.start(start);
            osc.stop(start + noteDuration);
          });
          this.connectingToneTimer = setTimeout(scheduleLoop, loopInterval * 1000);
        };
        scheduleLoop();
      });
    } catch {
      this.connectingToneCtx = null;
      this.connectingToneGain = null;
    }
  }

  private stopConnectingTone(): void {
    if (this.connectingToneTimer != null) {
      clearTimeout(this.connectingToneTimer);
      this.connectingToneTimer = null;
    }
    if (!this.connectingToneCtx) return;
    const ctx = this.connectingToneCtx;
    const gain = this.connectingToneGain;
    this.connectingToneCtx = null;
    this.connectingToneGain = null;
    try {
      if (gain) {
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      }
      setTimeout(() => ctx.close().catch(() => {}), 120);
    } catch {
      // already closed
    }
  }

  /**
   * Starts a soft pulsing tone (G4, 392 Hz) that plays after the backend signals
   * "listening" while waiting for the model's first audio response (Issue #1217).
   * The amplitude gently oscillates to convey "waiting for response."
   */
  private startAwaitingModelTone(ctxFactory: () => AudioContext = () => new AudioContext()): void {
    this.stopAwaitingModelTone();
    try {
      const ctx = ctxFactory();
      this.awaitingModelToneCtx = ctx;

      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.06;
      masterGain.connect(ctx.destination);
      this.awaitingModelToneGain = masterGain;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 392; // G4

      // LFO for gentle amplitude pulse (0.3–1.0 over ~1.2s cycle)
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      lfo.type = 'sine';
      lfo.frequency.value = 0.8; // ~1.2s cycle
      lfoGain.gain.value = 0.35;
      lfo.connect(lfoGain);

      const pulseGain = ctx.createGain();
      pulseGain.gain.value = 0.65;
      lfoGain.connect(pulseGain.gain);

      osc.connect(pulseGain);
      pulseGain.connect(masterGain);

      osc.start();
      lfo.start();
    } catch {
      this.awaitingModelToneCtx = null;
      this.awaitingModelToneGain = null;
    }
  }

  private stopAwaitingModelTone(): void {
    if (!this.awaitingModelToneCtx) return;
    const ctx = this.awaitingModelToneCtx;
    const gain = this.awaitingModelToneGain;
    this.awaitingModelToneCtx = null;
    this.awaitingModelToneGain = null;
    try {
      if (gain) {
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
      }
      setTimeout(() => ctx.close().catch(() => {}), 120);
    } catch {
      // already closed
    }
  }

  private stopAllConnectionTones(): void {
    this.stopConnectingTone();
    this.stopAwaitingModelTone();
  }

  /**
   * Plays a descending two-note "hangup" tone when the voice session ends.
   * Uses a standalone AudioContext because cleanup closes the playback pipeline.
   */
  private playHangupTone(ctxFactory: () => AudioContext = () => new AudioContext()): void {
    try {
      const ctx = ctxFactory();
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.09;
      masterGain.connect(ctx.destination);

      const notes = [659.25, 523.25]; // E5 → C5 — descending interval
      const noteDuration = 0.15;
      const gap = 0.05;

      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const env = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;

        const start = ctx.currentTime + i * (noteDuration + gap);
        env.gain.setValueAtTime(0, start);
        env.gain.linearRampToValueAtTime(1, start + 0.02);
        env.gain.linearRampToValueAtTime(0, start + noteDuration);

        osc.connect(env);
        env.connect(masterGain);
        osc.start(start);
        osc.stop(start + noteDuration);
      });

      const totalDuration = notes.length * (noteDuration + gap) + 0.1;
      setTimeout(() => ctx.close().catch(() => {}), totalDuration * 1000);
    } catch {
      // Audio not available — non-critical
    }
  }

  /** Short higher-frequency tone to signal a tool call completed. */
  private playCompletionTone(): void {
    if (!this.playbackContext || this.playbackContext.state === 'closed') return;
    const osc = this.playbackContext.createOscillator();
    const gain = this.playbackContext.createGain();
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.06;
    osc.connect(gain);
    gain.connect(this.playbackGain || this.playbackContext.destination);
    const now = this.playbackContext.currentTime;
    osc.start(now);
    osc.stop(now + 0.1);
  }

  /**
   * Resumes AudioContexts if the browser suspended them (common after prolonged
   * silence or tab backgrounding). Also verifies the WebSocket is still connected;
   * if the connection dropped while muted the user would otherwise unmute into a
   * dead session with no visible error.
   */
  private ensureAudioContextsRunning(): void {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      ClientLoggingService.log('WARN', `${LOG_TAG} Capture AudioContext suspended — resuming`);
      this.audioContext
        .resume()
        .catch(err => ClientLoggingService.log('ERROR', `${LOG_TAG} Failed to resume capture AudioContext`, err));
    }
    if (this.playbackContext && this.playbackContext.state === 'suspended') {
      ClientLoggingService.log('WARN', `${LOG_TAG} Playback AudioContext suspended — resuming`);
      this.playbackContext
        .resume()
        .catch(err => ClientLoggingService.log('ERROR', `${LOG_TAG} Failed to resume playback AudioContext`, err));
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      ClientLoggingService.log(
        'WARN',
        `${LOG_TAG} WebSocket not open on unmute (readyState=${this.ws?.readyState}) — triggering reconnect`
      );
      if (this.stateSubject.value === 'active' && !this.manualDisconnect && this.reconnectAttempts === 0) {
        this.scheduleReconnect();
      }
    }
  }

  private cleanup(): void {
    this.stopAllConnectionTones();
    this.cancelPendingReconnect();
    this.stopKeepalive();
    this.stopUiTimers();
    this.flushPlaybackBuffer();

    if (this.muteSub) {
      this.muteSub.unsubscribe();
      this.muteSub = null;
    }

    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    if (this.playbackGain) {
      this.playbackGain.disconnect();
      this.playbackGain = null;
    }

    if (this.playbackContext) {
      this.playbackContext.close().catch(() => {});
      this.playbackContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.backendReady = false;
    this.pendingAudioChunks = [];
    this.resetActivityBoundaryState();
    this.nextPlaybackTime = 0;
  }

  private uint8ToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToUint8(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
